import React, { useMemo, useState } from 'react';
import { ClipboardListIcon, SearchIcon } from 'lucide-react';
import { useHrPayroll } from '../../../contexts/HrPayrollContext';
import { formatDateTime } from '../../../utils/hrFormat';
import { exportCsv } from '../../../utils/hrExport';
import { Button } from '../../../components/ui/Button';
const PAGE = 50;

/** Append-only HR & payroll audit trail (salary changes, approvals, processing, reversals). */
export function HrAuditLogs() {
  const {
    auditLogs
  } = useHrPayroll();
  const [query, setQuery] = useState('');
  const [entity, setEntity] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const entities = Array.from(new Set(auditLogs.map(l => l.entity))).sort();
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return auditLogs.filter(l => !entity || l.entity === entity).filter(l => !q || [l.action, l.summary, l.userName, l.entityId].some(v => v?.toLowerCase().includes(q))).sort((a, b) => b.at.localeCompare(a.at));
  }, [auditLogs, entity, query]);
  return <section className="space-y-4" aria-label="HR audit logs" data-id="element-854" data-mp="2i7snxt4bbjkc">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-855" data-mp="2d87mnc1o6z4g">
        <div className="relative min-w-[220px] flex-1" data-id="element-856" data-mp="28ebu4p46e4go">
          <label htmlFor="audit-search" className="sr-only" data-id="element-857" data-mp="23hlc3w8t8ysm">Search audit log</label>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-858" data-mp="1yhqpi4w2ivdd" />
          <input id="audit-search" type="search" value={query} onChange={e => {
          setQuery(e.target.value);
          setLimit(PAGE);
        }} placeholder="Search action, details or user" className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm" data-id="element-859" data-mp="1uflxfal9q6su" />
        </div>
        <label className="text-sm" data-id="element-860" data-mp="3jv36tl30mzvt"><span className="sr-only" data-id="element-861" data-mp="3juaale1onsrx">Area</span>
          <select value={entity} onChange={e => {
          setEntity(e.target.value);
          setLimit(PAGE);
        }} className="rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-862" data-mp="3jsz6fd4bsj0a">
            <option value="" data-id="element-863" data-mp="1d5i088jvb0g8">All areas</option>
            {entities.map(e => <option key={e} data-id="element-864" data-mp="1hyb1gmg4nqnk">{e}</option>)}
          </select>
        </label>
        <Button variant="outline" disabled={rows.length === 0} onClick={() => exportCsv('HR_audit_log', [{
        key: 'at',
        label: 'Date/time'
      }, {
        key: 'user',
        label: 'User'
      }, {
        key: 'entity',
        label: 'Area'
      }, {
        key: 'action',
        label: 'Action'
      }, {
        key: 'summary',
        label: 'Details'
      }, {
        key: 'before',
        label: 'Before'
      }, {
        key: 'after',
        label: 'After'
      }], rows.map(l => ({
        at: formatDateTime(l.at),
        user: l.userName,
        entity: l.entity,
        action: l.action,
        summary: l.summary,
        before: l.before ?? '',
        after: l.after ?? ''
      })))} data-id="element-865" data-mp="138wwdqjl3vdz">
          Export CSV
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-866" data-mp="1qwmv6xcaj4k8">
        {rows.length === 0 ? <div className="py-14 text-center text-sm text-slate-500" data-id="element-867" data-mp="fjawt1zave5g">
            <ClipboardListIcon className="mx-auto mb-2 h-9 w-9 text-slate-300" aria-hidden="true" data-id="element-868" data-mp="fklwoqpmk1ra" />
            No audit entries match.
          </div> : <ul className="divide-y divide-slate-100" data-id="element-869" data-mp="2oqxjzx6yxq30">
            {rows.slice(0, limit).map(l => <li key={l.id} className="grid gap-1 px-5 py-3 text-sm sm:grid-cols-[170px_minmax(0,1fr)]" data-id="element-870" data-mp="32qqem3lm8qg9">
                <div className="text-xs text-slate-500" data-id="element-871" data-mp="2yrhnz7lvh8ox">
                  <p className="tabular-nums" data-id="element-872" data-mp="2b0c8itjugif5">{formatDateTime(l.at)}</p>
                  <p className="font-medium text-slate-700" data-id="element-873" data-mp="2ayhk359d686b">{l.userName}</p>
                </div>
                <div className="min-w-0" data-id="element-874" data-mp="25zeixvwi8m3m">
                  <p data-id="element-875" data-mp="2jt5rp22ew1ya"><span className="mr-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-700" data-id="element-876" data-mp="2jt5rb0yi1zxr">{l.entity}</span><span className="font-semibold text-slate-900" data-id="element-877" data-mp="2f14ijp0jirch">{l.action}</span></p>
                  <p className="mt-0.5 text-slate-700" data-id="element-878" data-mp="1rbtfg0vpwkv6">{l.summary}</p>
                  {(l.before || l.after) && <p className="mt-1 text-xs text-slate-500" data-id="element-879" data-mp="1na862pie6opq">
                      {l.before && <span className="line-through decoration-red-400" data-id="element-880" data-mp="2010d1n1drv8z">{l.before}</span>}
                      {l.before && l.after && <span className="mx-1.5" aria-hidden="true" data-id="element-881" data-mp="250d98ye56eo8">→</span>}
                      {l.after && <span className="text-emerald-700" data-id="element-882" data-mp="29y50rravjyrw">{l.after}</span>}
                    </p>}
                </div>
              </li>)}
          </ul>}
        {rows.length > limit && <div className="border-t border-slate-100 p-3 text-center" data-id="element-883" data-mp="2jnokt0abpyoc">
            <Button variant="ghost" size="sm" onClick={() => setLimit(n => n + PAGE)} data-id="element-884" data-mp="2np1r1bhbw3vs">Show more ({rows.length - limit} remaining)</Button>
          </div>}
      </div>
      <p className="text-xs text-slate-500" data-id="element-885" data-mp="2xnqzyer8ydzw">Audit entries are written in the same save as the change they describe and cannot be edited or deleted from the app.</p>
    </section>;
}